/**
 * waterLook — the CPU ORACLES of the water-realism terms (2026-09-25).
 *
 * Every formula the realistic water material evaluates in TSL for the
 * realism wave has its plain-number twin here, so unit tests and the Atlas
 * `verify:water-look` harness can pin the RULE (not the bug) without a GPU:
 *
 *  - R4 distance-aware glitter (Bruneton, Neyret & Holzschuch 2010,
 *    "Real-time Realistic Ocean Lighting using Seamless Transitions from
 *    Geometry to BRDF"): a wave whose wavelength falls under ~4 pixel
 *    footprints leaves the NORMAL and its slope variance moves into the
 *    microfacet roughness, α_eff² = α₀² + Σ faded σᵢ², σᵢ² = (kᵢAᵢ)²/2.
 *    The sun is a disc (Karis 2013, "Real Shading in Unreal Engine 4": the
 *    closest point on the source to the reflection ray + the (α/α′)²
 *    energy normalisation), 0.27° in angular radius.
 *  - R3 crest + body scattering (the Atlas / Sea of Thieves BSDF as
 *    GodotOceanWaves writes it): a backlit-crest term, a view-facing body
 *    term and a Lambert term, all tinted by the scatter colour.
 *  - R12 per-channel underwater absorption (Pope & Fry 1997 pure-water σₐ
 *    plus a small particle scattering σₛ): red dies first.
 *
 * No three.js import — unit-testable in plain Node.
 */

import type { GerstnerWave } from './GerstnerWaves.js';

/** The sun's angular radius (radians) — 0.27° (Karis' disc light for the glint). */
export const SUN_ANGULAR_RADIUS = 0.0047;

/**
 * The SURF BAND (surfaces agent, 2026-09-27) — the material's `beachFoam` before its noise gate: a DEPTH band
 * (1 − smoothstep(0, foamDepth, bedDepth)) × 0.9, and with `bandM` a WIDTH band too: the distance from the waterline,
 * bedDepth ÷ the bed's slope (m/m), fades out between bandM/2 and bandM. On a 2.5 % beach a 0.45 m depth band is 18 m
 * wide; a steep shore is depth-limited and unchanged.
 */
export function surfBand(bedDepth: number, slope: number, foamDepth: number, bandM = 0): number {
  const depthK = 1 - smoothstep01(0, foamDepth, bedDepth);
  if (!(bandM > 0)) return 0.9 * depthK;
  const dist = bedDepth / Math.max(slope, 0.002);
  return 0.9 * depthK * (1 - smoothstep01(bandM * 0.5, bandM, dist));
}

/** A wave leaves the normal between these many pixel footprints (smoothstep low → high). */
export const FOOTPRINT_FADE_LO = 2;
export const FOOTPRINT_FADE_HI = 4;

/** GLSL-style smoothstep. */
export function smoothstep01(e0: number, e1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0 || 1e-9)));
  return t * t * (3 - 2 * t);
}

/**
 * Slope variance (both axes summed) a Gerstner component adds to a surface:
 * its slope along its heading is kA·cos(phase), so the mean square slope is
 * (kA)²/2. `ampScale` is the sea state's amplitude modulation.
 */
export function waveSlopeVariance(wave: Pick<GerstnerWave, 'w' | 'amplitude'>, ampScale = 1): number {
  const s = wave.w * wave.amplitude * ampScale;
  return (s * s) / 2;
}

/** Total slope variance of a wave set (what a far pixel folds into its roughness). */
export function totalSlopeVariance(waves: ReadonlyArray<Pick<GerstnerWave, 'w' | 'amplitude'>>, ampScale = 1): number {
  let v = 0;
  for (const wv of waves) v += waveSlopeVariance(wv, ampScale);
  return v;
}

/**
 * How much of a wave stays in the NORMAL for a pixel footprint `f` (metres):
 * 1 while its wavelength spans ≥ 4 footprints, 0 under 2 (it would alias —
 * the classic glitter firefly), smooth between.
 */
export function footprintWeight(wavelength: number, footprint: number): number {
  const f = Math.max(1e-6, footprint);
  return smoothstep01(FOOTPRINT_FADE_LO * f, FOOTPRINT_FADE_HI * f, wavelength);
}

/**
 * The filtered GGX α² for a pixel: the authored α₀² plus the slope variance
 * of every wave (and of the detail maps) the footprint faded out of the
 * normal. `detailVariance` is the detail layers' own authored variance and
 * `detailWeight` the share of it still resolved in the normal.
 */
export function filteredAlpha2(
  alpha0: number,
  waves: ReadonlyArray<Pick<GerstnerWave, 'w' | 'amplitude' | 'wavelength'>>,
  footprint: number,
  ampScale = 1,
  detailVariance = 0,
  detailWeight = 1,
): number {
  let a2 = alpha0 * alpha0;
  for (const wv of waves) a2 += (1 - footprintWeight(wv.wavelength, footprint)) * waveSlopeVariance(wv, ampScale);
  a2 += (1 - Math.min(1, Math.max(0, detailWeight))) * detailVariance;
  return a2;
}

/**
 * Karis' sphere-light widening for a disc sun: the roughness a highlight
 * needs to cover the disc (α′ = α + θ/2) and the energy normalisation
 * (α/α′)² the NDF is multiplied by so the widened lobe carries the same energy.
 */
export function karisSunAlpha(alpha: number, angularRadius = SUN_ANGULAR_RADIUS): { alpha: number; normalization: number } {
  const a = Math.max(1e-4, alpha);
  const ap = Math.min(1, a + angularRadius * 0.5);
  return { alpha: ap, normalization: (a / ap) * (a / ap) };
}

/** Isotropic GGX D(n·h) for α (the material's distribution term). */
export function ggxD(ndh: number, alpha: number): number {
  const a2 = alpha * alpha;
  const d = ndh * ndh * (a2 - 1) + 1;
  return a2 / (Math.PI * d * d);
}

export interface ScatterParams {
  /** Scatter colour (linear HDR). */
  color: [number, number, number];
  /** k1 — the backlit-crest term. */
  crest: number;
  /** k2 — the view-facing body term. */
  body: number;
  /** k3 — the Lambert term. */
  lambert: number;
  /** h0 — added to the normalised crest height H ∈ [−1, 1] before max(0, ·). */
  heightBias: number;
  /**
   * The CHOP share of the crest's peak mask (surfaces agent, 2026-09-27; research §3.4): Sea of Thieves (Ang et al.,
   * SIGGRAPH 2018) masks the backlit-crest glow by the wave's CHOPPINESS — its horizontal displacement's convergence,
   * i.e. the surface compression 1 − J — not by height alone: a thin, sharp, folding crest lets the sun through, the
   * broad top of a swell does not. The mask = (H + h0) × mix(1 − chop, 1, smoothstep(0.05, 0.55, 1 − J)). 0 = height only.
   */
  chop?: number;
}

/** GodotOceanWaves' constants, rescaled to Atlas's normalised crest height (H ∈ [−1, 1]). */
export const DEFAULT_SCATTER: ScatterParams = {
  color: [0.05, 0.4, 0.35],
  crest: 1.0,
  body: 0.5,
  lambert: 0.5,
  heightBias: 1.0,
  chop: 0.65, // surfaces (2026-09-27): §3.4 — a broad swell's top keeps 35 % of the crest glow, a folding crest all of it
};
/** The crest peak mask's chop factor for a surface Jacobian `J` (1 flat … ≤ 0 folding) — see ScatterParams.chop. */
export function crestChopFactor(J: number, chop = DEFAULT_SCATTER.chop ?? 0): number {
  const c = 1 - J, t = Math.min(1, Math.max(0, (c - 0.05) / 0.5));
  return (1 - chop) + chop * t * t * (3 - 2 * t);
}

type V3 = [number, number, number];
const dot3 = (a: V3, b: V3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

/**
 * The scattering radiance multiplier (before scatter colour × sun colour ×
 * illuminance): crest + body + Lambert. `L` = unit direction TO the sun,
 * `V` = unit direction TO the viewer, `N` = the surface normal, `H` = the
 * normalised crest height (−1 trough … +1 crest).
 */
export function scatterWeight(L: V3, V: V3, N: V3, H: number, p: ScatterParams = DEFAULT_SCATTER, J?: number): number {
  const negV: V3 = [-V[0], -V[1], -V[2]];
  const back = Math.max(0, dot3(L, negV));
  const thin = 0.5 - 0.5 * dot3(L, N);
  // §3.4 (surfaces, 2026-09-27): with a Jacobian the peak mask reads the chop (the shader always passes one).
  const chopK = J === undefined ? 1 : crestChopFactor(J, p.chop ?? 0);
  const crest = p.crest * Math.max(0, H + p.heightBias) * chopK * back ** 4 * thin ** 3;
  const body = p.body * Math.max(0, dot3(V, N)) ** 2;
  const lamb = p.lambert * Math.max(0, dot3(L, N));
  return crest + body + lamb;
}

/** Schlick Fresnel for water (F0 0.02) — the share the SKY gets; scattering gets (1 − F). */
export function waterFresnel(cosTheta: number): number {
  return 0.02 + 0.98 * (1 - Math.max(0, cosTheta)) ** 5;
}

// ── R12: underwater, per channel ─────────────────────────────────────────────

/** Pope & Fry (1997) pure-water absorption at ~650 / 550 / 450 nm (1/m). */
// surfaces (2026-09-27, research §3.6): red was 0.45 — Pope & Fry's value at ~675 nm, not the 650 nm this names (0.340);
// against Lagarde / Monzon 2024's (0.35, 0.04, ~0.005) the red dies 30 % too fast and a shallow bed lost its warmth.
// Green (550 nm 0.0565) and blue (450 nm 0.0092) are Pope & Fry at their stated wavelengths and stay.
export const POPE_FRY_ABSORPTION: V3 = [0.34, 0.057, 0.009];
/** Suspended-particle scattering added for a coastal game sea (1/m, mildly blue-weighted). */
export const COASTAL_SCATTERING: V3 = [0.04, 0.045, 0.05];

/** Extinction σₜ = σₐ + σₛ per channel. */
export function extinction(absorb: V3 = POPE_FRY_ABSORPTION, scatter: V3 = COASTAL_SCATTERING): V3 {
  return [absorb[0] + scatter[0], absorb[1] + scatter[1], absorb[2] + scatter[2]];
}

/** Beer–Lambert transmittance over `d` metres, per channel. */
export function transmittance(d: number, sigmaT: V3 = extinction()): V3 {
  return [Math.exp(-sigmaT[0] * d), Math.exp(-sigmaT[1] * d), Math.exp(-sigmaT[2] * d)];
}

/** Single-scattering albedo σₛ/σₜ per channel (what colour the medium glows in-scattered sun). */
export function scatterAlbedo(absorb: V3 = POPE_FRY_ABSORPTION, scatter: V3 = COASTAL_SCATTERING): V3 {
  const t = extinction(absorb, scatter);
  return [scatter[0] / t[0], scatter[1] / t[1], scatter[2] / t[2]];
}

/**
 * What a submerged camera sees of a surface `d` metres away with radiance
 * `surface` (per channel): the surface attenuated plus the medium's
 * in-scatter of the sun, itself dimmed by the camera's own depth.
 */
export function underwaterComposite(surface: V3, d: number, camDepth: number, sun = 1, absorb: V3 = POPE_FRY_ABSORPTION, scatter: V3 = COASTAL_SCATTERING): V3 {
  const st = extinction(absorb, scatter);
  const T = transmittance(d, st);
  const alb = scatterAlbedo(absorb, scatter);
  const down = transmittance(camDepth, st);
  return [0, 1, 2].map((c) => surface[c] * T[c] + alb[c] * sun * down[c] * (1 - T[c])) as V3;
}

// ── R14: the rainbow in waterfall mist ──────────────────────────────────────

/**
 * Rainbow colour weight for a view ray at `thetaDeg` from the ANTISOLAR point
 * (the point opposite the sun): the primary bow 40.5–42.5° (red outside), the
 * secondary 50–53° reversed at 0.4. Returns linear RGB (0 outside the bows).
 */
export function rainbowRgb(thetaDeg: number): V3 {
  const band = (t: number, lo: number, hi: number, reverse: boolean, gain: number): V3 => {
    if (t < lo - 0.6 || t > hi + 0.6) return [0, 0, 0];
    let u = (t - lo) / (hi - lo); // 0 = inner edge … 1 = outer edge
    if (reverse) u = 1 - u;
    const edge = smoothstep01(-0.25, 0.1, u) * (1 - smoothstep01(0.9, 1.25, u));
    // violet/blue inside → red outside (primary)
    const r = smoothstep01(0.45, 0.95, u);
    const g = Math.max(0, 1 - Math.abs(u - 0.55) * 2.4);
    const b = 1 - smoothstep01(0.05, 0.5, u);
    return [r * edge * gain, g * edge * gain, b * edge * gain];
  };
  const p = band(thetaDeg, 40.5, 42.5, false, 1);
  const s = band(thetaDeg, 50, 53, true, 0.4);
  return [p[0] + s[0], p[1] + s[1], p[2] + s[2]];
}

/** The bow shows only while the sun is below 42° (the antisolar point below the horizon by less than the bow). */
export function rainbowSunGate(sunElevationDeg: number): number {
  return 1 - smoothstep01(36, 42, sunElevationDeg);
}

// ── The waterline on the near plane (shader gaps R1, 2026-09-29) ─────────────
//
// A camera AT the surface sees two worlds: pixels whose near-plane point lies under the water look through the
// medium, the rest through air. The old switch flipped the whole image on the camera ORIGIN's height against a flat
// level. The near plane is a few centimetres across (0.05 m × tan(fov/2)), far smaller than any wave, so the water
// there is a PLANE (the displaced surface's height and slope at the camera) and its intersection with the near plane
// is a straight line on screen — Crest's horizon mask (`_HorizonPosNormal`, crest-ocean/crest PR #299: the water plane
// projected into screen space as a point + normal) and meniscus (`UnderwaterMeniscus.shader`: a thin band where the
// surface meets the near plane). The signed height of the near-plane point behind screen position (x, y) in NDC is
//     s(x, y) = N·(C + n·F − S) + x·n·tan(fov/2)·aspect·(N·R) + y·n·tan(fov/2)·(N·U)
// — LINEAR in (x, y) — with C the eye, F/R/U its forward/right/up, n the near distance, S a surface point under the eye
// and N the surface normal. `nearPlaneWaterline` returns that line in TOP-origin screen UV (THE POST-UV LAW: NDC
// y = 1 − 2v), normalised to units of the screen HEIGHT so an edge or a meniscus has the same width at any resolution.

/** The waterline's state: every near-plane corner above the water, every one below, or the plane cut by it. */
export type WaterlineState = 'above' | 'below' | 'split';

export interface NearPlaneWaterlineInput {
  /** The eye (any frame consistent with `surfaceY`, metres). */
  cam: V3;
  /** Unit camera basis: right (+X of the view), up (+Y) and forward (the view direction, −Z of the view). */
  right: V3;
  up: V3;
  forward: V3;
  /** Near-plane distance (m), tan of half the VERTICAL field of view, and width / height. */
  near: number;
  tanHalfFovY: number;
  aspect: number;
  /** The water surface's height under the eye (m) and its unit normal there (default straight up). */
  surfaceY: number;
  normal?: V3;
}

export interface NearPlaneWaterline {
  state: WaterlineState;
  /** Signed height (m, + = above the water) of the near-plane corners at NDC (−1,−1), (1,−1), (1,1), (−1,1). */
  corners: [number, number, number, number];
  /**
   * The line in top-origin screen UV: d(u, v) = line[0] + line[1]·u + line[2]·v is the signed distance from the
   * waterline in screen HEIGHTS (negative = under the water). Meaningful for every state (a whole-screen state has the
   * line off screen); a degenerate plane (the near plane parallel to the surface) returns ±1e3 constants.
   */
  line: [number, number, number];
}

/** The near-plane waterline for a camera and the water plane under it (see the section header). */
export function nearPlaneWaterline(i: NearPlaneWaterlineInput): NearPlaneWaterline {
  let N: V3 = i.normal ?? [0, 1, 0];
  const nl = Math.hypot(N[0], N[1], N[2]);
  N = nl > 1e-9 ? [N[0] / nl, N[1] / nl, N[2] / nl] : [0, 1, 0];
  const hh = i.near * i.tanHalfFovY;
  const hw = hh * i.aspect;
  // a = N·(C + n·F − S) with S = (C.x, surfaceY, C.z): N·(C − S) = N.y·(C.y − surfaceY).
  const a = N[1] * (i.cam[1] - i.surfaceY) + i.near * dot3(N, i.forward);
  const b = hw * dot3(N, i.right);
  const c = hh * dot3(N, i.up);
  const s = (x: number, y: number): number => a + b * x + c * y;
  const corners: [number, number, number, number] = [s(-1, -1), s(1, -1), s(1, 1), s(-1, 1)];
  const lo = Math.min(...corners), hi = Math.max(...corners);
  const state: WaterlineState = hi < 0 ? 'below' : lo > 0 ? 'above' : 'split';
  // s(u, v) with x = 2u − 1, y = 1 − 2v: (a − b + c) + 2b·u − 2c·v; its gradient in screen-height units (X = u·aspect, Y = v).
  const gx = (2 * b) / Math.max(i.aspect, 1e-6), gy = -2 * c;
  const g = Math.hypot(gx, gy);
  if (!(g > 1e-12)) return { state, corners, line: [a >= 0 ? 1e3 : -1e3, 0, 0] };
  return { state, corners, line: [(a - b + c) / g, (2 * b) / g, (-2 * c) / g] };
}

/** Screen heights over which the water / air edge is antialiased (≈ 1.5 px at 1080p). */
export const WATERLINE_EDGE = 0.0014;
/** The meniscus band's half-width (screen heights) — Crest's line is a few pixels. */
export const MENISCUS_WIDTH = 0.0045;

/** The shader's water cover at top-origin screen UV (u, v): 1 = under the water, 0 = air, smooth over ±edge. */
export function waterlineCover(line: readonly [number, number, number], u: number, v: number, edge = WATERLINE_EDGE): number {
  const d = line[0] + line[1] * u + line[2] * v;
  return 1 - smoothstep01(-edge, edge, d);
}

/** The meniscus band's strength at (u, v): 1 on the line, 0 past `width`. */
export function meniscusBand(line: readonly [number, number, number], u: number, v: number, width = MENISCUS_WIDTH): number {
  const d = Math.abs(line[0] + line[1] * u + line[2] * v);
  return 1 - smoothstep01(0, width, d);
}
